import React from "react";

/**
 * One screen crashing must not leave a candidate on a white page (UI audit 2026-10-16, docs/30 D6).
 *
 * Nothing caught a render error in this app: a crash anywhere — mid-lobby, on a result — blanked the whole app with
 * no message and no way back but reload. The pattern of KIITx's portals (RouteErrorBoundary around every route):
 * App.js wraps its routes in it, keyed by the path, so the next navigation clears it. A paper's answers are kept by
 * the journal on the device regardless (docs/08); this is about the screen.
 */
const isDev = process.env.NODE_ENV === "development";

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[route] render failed:", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.routeKey !== this.props.routeKey) this.setState({ error: null });
  }

  handleRetry() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6" role="alert" data-testid="route-error">
        <div className="max-w-xl w-full rounded-2xl border border-rose-200 bg-rose-50 p-6">
          <h2 className="text-base font-semibold text-rose-900">This screen could not be displayed</h2>
          <p className="mt-1 text-sm text-rose-800">
            Something on this screen failed. If you were writing an exam, your answers are saved on this device — try
            again, or go back.
          </p>
          <p className="mt-3 rounded-lg bg-white/70 p-2 font-mono text-xs text-rose-900">{error?.message || "Unknown error"}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={this.handleRetry} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700">
              Try again
            </button>
            <button type="button" onClick={() => window.history.back()} className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-800">
              Go back
            </button>
          </div>
          {isDev && error?.stack ? (
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-white/70 p-2 text-[11px] text-rose-900">{error.stack}</pre>
          ) : null}
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
